import { BigintIsh, CurrencyAmount } from '@kodiak-finance/sdk-core'
import REWARDS_ABI from 'abis/KodiakRewards.json'
import XKDK_ABI from 'abis/XKDK.json'
import { xKDKAbi } from 'abis/xKDKAbi'
import { REFETCH_POLICY_FAST, RefetchPolicy } from 'config/RefetchPolicies'
import { KDK_ADDRESSES, KODIAK_REWARDS_ADDRESSES, XKDK_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import { useCallback, useMemo } from 'react'
import { isAddress } from 'utils'
import type { Abi } from 'viem'
import { Address } from 'viem'
import { useReadContract } from 'wagmi'

import { useToken, useTokens } from './Tokens'
import { useReadContractMultiple } from './useReadContractMultiple'

interface StakingQueryOptions {
  refetchPolicy?: RefetchPolicy
}

export const useDividends = (options?: StakingQueryOptions) => {
  const { account, chainId } = useConnection()
  const rewardsAddress = KODIAK_REWARDS_ADDRESSES[chainId]
  const xKdkAddress = XKDK_ADDRESSES[chainId]
  const xKdkToken = useToken(xKdkAddress)
  const refetchPolicy = options?.refetchPolicy

  // Batch read rewards contract calls (no args)
  const rewardsContractsNoArgs = useMemo(() => {
    if (!rewardsAddress) return []
    return [
      {
        address: rewardsAddress,
        abi: REWARDS_ABI as Abi,
        functionName: 'nextCycleStartTime',
      },
      {
        address: rewardsAddress,
        abi: REWARDS_ABI as Abi,
        functionName: 'cycleDurationSeconds' as const,
      },
      {
        address: rewardsAddress,
        abi: REWARDS_ABI as Abi,
        functionName: 'distributedTokensLength',
      },
      {
        address: rewardsAddress,
        abi: REWARDS_ABI as Abi,
        functionName: 'totalAllocation',
      },
    ]
  }, [rewardsAddress])

  const { data: rewardsResultsNoArgs, refetch: refetchRewardsNoArgs } = useReadContractMultiple({
    contracts: rewardsContractsNoArgs,
    query: {
      ...(refetchPolicy ?? REFETCH_POLICY_FAST),
      enabled: rewardsContractsNoArgs.length > 0,
    },
  })

  const nextCycleStartTimeResult = rewardsResultsNoArgs?.[0]?.result
  const cycleDurationSecondsResult = rewardsResultsNoArgs?.[1]?.result
  const distributedTokensLengthResult = rewardsResultsNoArgs?.[2]?.result
  const totalAllocationResult = rewardsResultsNoArgs?.[3]?.result

  // Batch read rewards contract calls (with account arg)
  const rewardsContractsWithAccount = useMemo(() => {
    if (!rewardsAddress || !account) return []
    return [
      {
        address: rewardsAddress,
        abi: REWARDS_ABI as Abi,
        functionName: 'usersAllocation',
        args: [account],
      },
    ]
  }, [rewardsAddress, account])

  const { data: rewardsResultsWithAccount, refetch: refetchRewardsWithAccount } = useReadContractMultiple<bigint>({
    contracts: rewardsContractsWithAccount,
    query: { ...(refetchPolicy ?? REFETCH_POLICY_FAST), enabled: rewardsContractsWithAccount.length > 0 },
  })

  const usersAllocationResult = rewardsResultsWithAccount?.[0]?.result

  const { data: usagesDeallocationFeeResult, refetch: refetchUsagesDeallocationFee } = useReadContract({
    address: xKdkAddress,
    abi: xKDKAbi,
    functionName: 'usagesDeallocationFee',
    args: [rewardsAddress],
    query: refetchPolicy,
  })

  const { data: userUsageAllocationResult, refetch: refetchUserUsageAllocation } = useReadContract({
    address: xKdkAddress,
    abi: xKDKAbi,
    functionName: 'getUsageAllocation',
    args: [account as Address, rewardsAddress],
    query: { ...(refetchPolicy ?? REFETCH_POLICY_FAST), enabled: !!account },
  })

  const usagesDeallocationFee = useMemo(() => {
    const fee =
      usagesDeallocationFeeResult !== undefined ? parseFloat(usagesDeallocationFeeResult.toString()) / 100 : undefined
    return fee !== undefined && !isNaN(fee) ? fee : undefined
  }, [usagesDeallocationFeeResult])

  const userUsageAllocation = useMemo(
    () =>
      xKdkToken && userUsageAllocationResult !== undefined
        ? CurrencyAmount.fromRawAmount(xKdkToken, userUsageAllocationResult.toString())
        : undefined,
    [userUsageAllocationResult, xKdkToken]
  )

  const distributedTokensIds = useMemo(() => {
    const len = distributedTokensLengthResult ? parseFloat(distributedTokensLengthResult.toString()) : 0
    const indexes = Array.from({ length: len }, (_, index) => index)
    return indexes
  }, [distributedTokensLengthResult])

  const distributedTokensContracts = useMemo(() => {
    if (!rewardsAddress || distributedTokensIds.length === 0) return []
    return distributedTokensIds.map((index) => ({
      address: rewardsAddress,
      abi: REWARDS_ABI as Abi,
      functionName: 'distributedToken',
      args: [BigInt(index)],
    }))
  }, [rewardsAddress, distributedTokensIds])

  const { data: distributedTokensRaw, refetch: refetchDistributedTokens } = useReadContractMultiple({
    contracts: distributedTokensContracts,
    query: { ...refetchPolicy, enabled: distributedTokensContracts.length > 0 },
  })
  const distributedTokens = useMemo(() => {
    if (!distributedTokensRaw) return []
    return distributedTokensRaw.map((result) => {
      if (result.status === 'success' && result.result) {
        return result.result as string
      }
      return undefined
    })
  }, [distributedTokensRaw])

  const totalAllocation = useMemo(
    () =>
      xKdkToken && totalAllocationResult !== undefined && totalAllocationResult !== null
        ? CurrencyAmount.fromRawAmount(xKdkToken, totalAllocationResult.toString())
        : undefined,
    [totalAllocationResult, xKdkToken]
  )

  const formattedTokens = useMemo(
    () =>
      distributedTokens
        .map((addr) => (isAddress(addr) ? addr : undefined))
        .filter((token) => typeof token === 'string') as string[],
    [distributedTokens]
  )

  const tokens = useTokens(formattedTokens)

  const rewardsInfoContracts = useMemo(() => {
    if (!rewardsAddress || formattedTokens.length === 0) return []
    return formattedTokens.map((token) => ({
      address: rewardsAddress,
      abi: REWARDS_ABI as Abi,
      functionName: 'rewardsInfo',
      args: [token as Address],
    }))
  }, [rewardsAddress, formattedTokens])

  const { data: rewardsInfoRaw, refetch: refetchRewardsInfo } = useReadContractMultiple<
    [
      bigint, // currentDistributionAmount
      bigint, // currentCycleDistributedAmount
      bigint, // pendingAmount
      bigint, // distributedAmount
      bigint, // accRewardsPerShare
      bigint, // lastUpdateTime
      bigint, // cycleRewardsPercent,
      boolean // distributionDisabled
    ]
  >({
    contracts: rewardsInfoContracts,
    query: { ...refetchPolicy, enabled: rewardsInfoContracts.length > 0 },
  })

  const pendingRewardsAmountContracts = useMemo(() => {
    if (!rewardsAddress || !account || formattedTokens?.length === 0) return []
    return formattedTokens.map((token) => ({
      address: rewardsAddress,
      abi: REWARDS_ABI as Abi,
      functionName: 'pendingRewardsAmount',
      args: [token as Address, account],
    }))
  }, [rewardsAddress, account, formattedTokens])

  const { data: pendingRewardsAmountRaw, refetch: refetchPendingRewardsAmount } = useReadContractMultiple<bigint>({
    contracts: pendingRewardsAmountContracts,
    query: { ...refetchPolicy, enabled: pendingRewardsAmountContracts.length > 0 },
  })

  const refresh = useCallback(async () => {
    await Promise.allSettled([
      refetchRewardsNoArgs(),
      refetchRewardsWithAccount(),
      refetchUsagesDeallocationFee(),
      refetchUserUsageAllocation(),
      refetchDistributedTokens(),
      refetchRewardsInfo(),
      refetchPendingRewardsAmount(),
    ])
  }, [
    refetchDistributedTokens,
    refetchPendingRewardsAmount,
    refetchRewardsInfo,
    refetchRewardsNoArgs,
    refetchRewardsWithAccount,
    refetchUsagesDeallocationFee,
    refetchUserUsageAllocation,
  ])

  const rewardsInfo = useMemo(() => {
    if (!rewardsInfoRaw || !tokens) return []
    return rewardsInfoRaw.map((result, index) => {
      if (result.status !== 'success' || !result.result) return undefined
      if (!tokens || tokens[index] === null) return undefined

      const [
        currentDistributionAmount,
        currentCycleDistributedAmount,
        pendingAmount,
        distributedAmount,
        _accRewardsPerShare,
        _lastUpdateTime,
        cycleRewardsPercent,
      ] = result.result

      const token = tokens[index]
      if (!token) return undefined

      return {
        distributedAmount: CurrencyAmount.fromRawAmount(token, distributedAmount.toString()),
        currentDistributionAmount: CurrencyAmount.fromRawAmount(token, currentDistributionAmount.toString()),
        currentCycleDistributedAmount: CurrencyAmount.fromRawAmount(token, currentCycleDistributedAmount.toString()),
        pendingAmount: CurrencyAmount.fromRawAmount(token, pendingAmount.toString()),
        cycleRewardsPercent,
      }
    })
  }, [rewardsInfoRaw, tokens])

  const pendingRewardsAmount = useMemo(() => {
    if (!pendingRewardsAmountRaw || !tokens) return []
    return pendingRewardsAmountRaw.map((result, index) => {
      if (result.status !== 'success' || result.result === undefined) return undefined
      const token = tokens[index]
      if (!token) return undefined

      return CurrencyAmount.fromRawAmount(token, result.result.toString())
    })
  }, [tokens, pendingRewardsAmountRaw])

  const userAllocation = useMemo(
    () =>
      xKdkToken && usersAllocationResult !== undefined
        ? CurrencyAmount.fromRawAmount(xKdkToken, usersAllocationResult.toString())
        : undefined,
    [usersAllocationResult, xKdkToken]
  )

  const nextCycleStartTime = useMemo(
    () => (nextCycleStartTimeResult ? parseFloat(nextCycleStartTimeResult.toString()) : 0),
    [nextCycleStartTimeResult]
  )
  const cycleDurationSeconds = useMemo(
    () => (cycleDurationSecondsResult ? parseFloat(cycleDurationSecondsResult.toString()) : 0),
    [cycleDurationSecondsResult]
  )

  return {
    deallocationFee: usagesDeallocationFee,
    totalAllocation,
    nextCycleStartTime,
    userAllocation,
    userUsageAllocation,
    distributedTokens: tokens,
    distributedTokenAddresses: formattedTokens,
    rewardsInfo,
    pendingRewardsAmount,
    cycleDurationSeconds,
    refresh,
  }
}

export const useStaking = (options?: StakingQueryOptions) => {
  const { account, chainId } = useConnection()
  const xkdkAddress = XKDK_ADDRESSES[chainId]
  const kdkAddress = KDK_ADDRESSES[chainId]
  const xKdkToken = useToken(xkdkAddress)
  const kdkToken = useToken(kdkAddress)
  const fastPolicy = options?.refetchPolicy ?? REFETCH_POLICY_FAST

  // Batch read XKDK contract calls
  const xkdkContracts = useMemo(() => {
    if (!xkdkAddress) return []
    const contracts: Array<{
      address: Address
      abi: Abi
      functionName: string
      args?: Address[]
    }> = [
      {
        address: xkdkAddress,
        abi: XKDK_ABI as Abi,
        functionName: 'minRedeemDuration',
      },
      {
        address: xkdkAddress,
        abi: XKDK_ABI as Abi,
        functionName: 'maxRedeemDuration',
      },
    ]
    if (account) {
      contracts.push(
        {
          address: xkdkAddress,
          abi: XKDK_ABI as Abi,
          functionName: 'balanceOf',
          args: [account],
        },
        {
          address: xkdkAddress,
          abi: XKDK_ABI as Abi,
          functionName: 'getUserRedeemsLength',
          args: [account],
        },
        {
          address: xkdkAddress,
          abi: XKDK_ABI as Abi,
          functionName: 'getXKodiakBalance',
          args: [account],
        }
      )
    }
    return contracts
  }, [xkdkAddress, account])

  const { data: xkdkResults, refetch: refetchXkdkData } = useReadContractMultiple({
    contracts: xkdkContracts,
    query: { ...fastPolicy, enabled: xkdkContracts.length > 0, keepPreviousData: true },
  })

  // Extract results based on whether account is available
  const minRedeemDurationResult = xkdkResults?.[0]?.result
  const maxRedeemDurationResult = xkdkResults?.[1]?.result
  const balanceResult = account ? xkdkResults?.[2]?.result : undefined
  const getUserRedeemsLengthResult = account ? xkdkResults?.[3]?.result : undefined
  const getXKodiakBalanceResult = account ? xkdkResults?.[4]?.result : undefined

  const redeems = useMemo(() => {
    const redeemsLen = getUserRedeemsLengthResult ? parseFloat(getUserRedeemsLengthResult.toString()) : 0
    const redeemsIndexes = Array.from({ length: redeemsLen }, (_, index) => index)
    return redeemsIndexes
  }, [getUserRedeemsLengthResult])

  const userRedeemsContracts = useMemo(() => {
    if (!xkdkAddress || !account || redeems.length === 0) return []
    return redeems.map((index) => ({
      address: xkdkAddress,
      abi: XKDK_ABI as Abi,
      functionName: 'getUserRedeem' as const,
      args: [account, BigInt(index)],
    }))
  }, [xkdkAddress, account, redeems])

  const { data: userRedeemsRaw, refetch: refetchUserRedeems } = useReadContractMultiple({
    contracts: userRedeemsContracts,
    query: { ...fastPolicy, enabled: userRedeemsContracts.length > 0, keepPreviousData: true },
  })

  const refresh = useCallback(async () => {
    await Promise.allSettled([refetchXkdkData(), refetchUserRedeems()])
  }, [refetchUserRedeems, refetchXkdkData])

  const userRedeems = useMemo(
    () =>
      userRedeemsRaw
        ?.map((result, index) => {
          if (result.status !== 'success' || !result.result || !xKdkToken || !kdkToken) return undefined
          const redeem = result.result as [bigint, bigint, bigint, string, bigint]
          return {
            kdkAmount: CurrencyAmount.fromRawAmount(kdkToken, redeem[0].toString()),
            xKdkAmount: CurrencyAmount.fromRawAmount(xKdkToken, redeem[1].toString()),
            endTime: parseFloat(redeem[2].toString() || '0'),
            rewardsContract: redeem[3] as string,
            rewardsAllocation: redeem[4].toString() as BigintIsh,
            index,
          }
        })
        .filter((item) => item !== undefined) ?? [],
    [userRedeemsRaw, xKdkToken, kdkToken]
  )

  if (
    !xKdkToken ||
    balanceResult === undefined ||
    !minRedeemDurationResult ||
    !maxRedeemDurationResult ||
    !getXKodiakBalanceResult
  ) {
    return {
      minRedeemDuration: minRedeemDurationResult ? parseFloat(minRedeemDurationResult.toString()) : undefined,
      maxRedeemDuration: maxRedeemDurationResult ? parseFloat(maxRedeemDurationResult.toString()) : undefined,
      refresh,
    }
  }

  const balance = balanceResult as bigint
  const xKodiakBalance = getXKodiakBalanceResult as [bigint, bigint]

  return {
    available: CurrencyAmount.fromRawAmount(xKdkToken, balance.toString()),
    allocated: CurrencyAmount.fromRawAmount(xKdkToken, xKodiakBalance[0].toString()),
    redeeming: CurrencyAmount.fromRawAmount(xKdkToken, xKodiakBalance[1].toString()),
    minRedeemDuration: parseFloat(minRedeemDurationResult.toString()),
    maxRedeemDuration: parseFloat(maxRedeemDurationResult.toString()),
    redeems: userRedeems,
    refresh,
  }
}

export default useStaking
